import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Tooltip, CircularProgress, Typography, Divider, MenuItem, Tabs, Tab, useTheme
} from "@material-ui/core";
import Skeleton from '@material-ui/lab/Skeleton';
import { Add, Close, Publish, Refresh, Save } from "@material-ui/icons";
import { createRelease, listReleases, getRelease, updateRelease, publishRelease, unpublishRelease, forceDeleteRelease, addItem, updateItem, deleteItem, deleteRelease, listRoadmap, createRoadmapItem, updateRoadmapItem, deleteRoadmapItem, addItemAttachments, deleteItemAttachment, exportZip as exportReleasesZip, importZip as importReleasesZip } from "../../services/changelogAdmin";
import toastError from "../../errors/toastError";
import toastSuccess from "../../errors/toastSuccess";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import Markdown from "markdown-to-jsx";

// MVP: listagem de releases (publicadas + se quiser drafts após abrir). Como listPublic não retorna drafts, após criar draft o próprio create retorna e inserimos manualmente.
// Para edições e itens sempre carregamos via getRelease.

// Aceitar apenas formato numérico estrito X.X.X (sem prerelease/build)
const semverRegex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

const categorias = [
  { value: 'feature', label: 'Feature' },
  { value: 'improvement', label: 'Melhoria' },
  { value: 'fix', label: 'Correção' },
  { value: 'security', label: 'Segurança' },
  { value: 'breaking', label: 'Breaking' },
  { value: 'deprecated', label: 'Deprecação' },
  { value: 'internal', label: 'Interno' },
];

const ReleasesAdmin = ({ user }) => {
  const { user: ctxUser } = useContext(AuthContext);
  const currentUser = user || ctxUser;
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState(null); // release completa
  const [version, setVersion] = useState("");
  const [titulo, setTitulo] = useState("");
  const [desc, setDesc] = useState("");
  const [savingRelease, setSavingRelease] = useState(false);
  const [versionError, setVersionError] = useState("");
  // Export/Import state
  const [openExport, setOpenExport] = useState(false);
  const [exportMode, setExportMode] = useState("all"); // all | last
  const [exportCount, setExportCount] = useState(3);
  const [exporting, setExporting] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Item modal
  const [openItemModal, setOpenItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemTitulo, setItemTitulo] = useState("");
  const [itemCategoria, setItemCategoria] = useState("feature");
  const [itemMarkdown, setItemMarkdown] = useState("");
  const [itemFiles, setItemFiles] = useState([]);
  const [savingItem, setSavingItem] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [failedThumbs, setFailedThumbs] = useState({}); // { [key: string]: true }
  const [thumbAttempt, setThumbAttempt] = useState({}); // { [key: string]: number }

  // Roadmap state
  const [roadmap, setRoadmap] = useState([]);
  const [loadingRoadmap, setLoadingRoadmap] = useState(false);
  const [openRoadmapModal, setOpenRoadmapModal] = useState(false);
  const [editingRoadmapItem, setEditingRoadmapItem] = useState(null);
  const [rmTitulo, setRmTitulo] = useState("");
  const [rmDesc, setRmDesc] = useState("");
  const [rmStatus, setRmStatus] = useState("planned");
  const [rmPrioridade, setRmPrioridade] = useState("media");
  const [savingRoadmap, setSavingRoadmap] = useState(false);

  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const borderColor = theme.palette.divider;

  // Permissão super: fallback caso a página seja acessada sem checagem externa
  // (Proteção agora aplicada na rota via requireSuper, mantendo apenas fallback leve)
  useEffect(() => {
    if (currentUser && currentUser.super !== true) {
      try { window.location.replace('/'); } catch {}
    }
  }, [currentUser]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listReleases({ limit: 50, offset: 0 });
      setReleases(data.releases || []);
    } catch (e) { toastError(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  // Prefetch roadmap light para deixar aba instantânea
  useEffect(() => {
    (async () => {
      try { const data = await listRoadmap({ light: true }); setRoadmap(data); } catch(e) { /* silencioso */ }
    })();
  }, []);

  const loadRoadmap = async () => {
    setLoadingRoadmap(true);
    try { const data = await listRoadmap({ light: true }); setRoadmap(data); } catch(e){ toastError(e);} finally { setLoadingRoadmap(false); }
  };
  useEffect(() => { if (tab===1 && roadmap.length === 0) loadRoadmap(); }, [tab]);

  const handleNew = () => {
    setEditing(null);
    setVersion("");
    setVersionError("");
    setTitulo("");
    setDesc("");
    setOpenEditor(true);
  };

  const handleEdit = async (rel) => {
    try {
      const full = await getRelease(rel.id);
      setEditing(full);
      setVersion(full.version || "");
      setVersionError("");
      setTitulo(full.titulo || "");
      setDesc(full.descricaoMarkdown || "");
      setOpenEditor(true);
    } catch (e) { toastError(e); }
  };

  const handleSaveRelease = async () => {
    if (!semverRegex.test(version)) {
      setVersionError("Use o formato X.X.X (ex.: 1.2.3)");
      toastError("Versão inválida. Use o formato X.X.X (ex.: 1.2.3)");
      return;
    }
    setSavingRelease(true);
    try {
      if (!editing) {
        const created = await createRelease({ version, titulo });
        // drafts não aparecem na listagem pública -> inserir manualmente na lista local
        setReleases(prev => [created, ...prev]);
        toastSuccess("Release criada");
        // Carrega completa para permitir itens
        const full = await getRelease(created.id);
        setEditing(full);
      } else {
        const upd = await updateRelease(editing.id, { titulo });
        toastSuccess("Release atualizada");
        setEditing(prev => ({ ...prev, ...upd }));
        setReleases(prev => prev.map(r => r.id === upd.id ? { ...r, ...upd } : r));
      }
    } catch (e) { toastError(e); }
    finally { setSavingRelease(false); }
  };

  const handlePublish = async () => {
    if (!editing) return;
    if (!editing.itens || editing.itens.length === 0) {
      toastError("Adicione ao menos 1 item antes de publicar");
      return;
    }
    try {
      const pub = await publishRelease(editing.id);
      toastSuccess("Release publicada");
      setEditing(prev => ({ ...prev, ...pub }));
      setReleases(prev => prev.map(r => r.id === pub.id ? { ...r, ...pub } : r));
    } catch (e) { toastError(e); }
  };

  const handleDeleteRelease = async () => {
    if (!editing) return;
    if (editing.status !== 'draft') {
      toastError('Só é permitido excluir drafts.');
      return;
    }
    const conf = window.prompt(`Digite a versão "${editing.version}" para confirmar exclusão:`);
    if (conf === null) { // cancelou
      return;
    }
    if (conf !== editing.version) {
      toastError('Confirmação não confere.');
      return;
    }
    try {
      await deleteRelease(editing.id);
      toastSuccess('Release excluída');
      setReleases(prev => prev.filter(r => r.id !== editing.id));
      setOpenEditor(false);
      setEditing(null);
    } catch (e) { toastError(e); }
  };

  const handleUnpublish = async () => {
    if (!editing || editing.status !== 'published') return;
    const ok = window.confirm(`Cancelar publicação da versão ${editing.version}? Ela voltará a ser Draft.`);
    if (!ok) return;
    try {
      const res = await unpublishRelease(editing.id);
      toastSuccess('Publicação cancelada');
      setEditing(prev => ({ ...prev, ...res }));
      setReleases(prev => prev.map(r => r.id === res.id ? { ...r, ...res } : r));
    } catch (e) { toastError(e); }
  };

  const handleForceDelete = async () => {
    if (!editing || editing.status !== 'published') return;
    const conf1 = window.prompt(`Para excluir DEFINITIVAMENTE a release publicada digite a versão exata: ${editing.version}`);
    if (conf1 === null) { // cancelou no prompt
      return;
    }
    if (conf1 !== editing.version) {
      toastError('Versão não confere. Operação abortada.');
      return;
    }
    const conf2 = window.confirm('Tem certeza? Esta ação é irreversível e removerá a release dos usuários.');
    if (!conf2) return;
    try {
      await forceDeleteRelease(editing.id);
      toastSuccess('Release publicada excluída');
      setReleases(prev => prev.filter(r => r.id !== editing.id));
      setOpenEditor(false);
      setEditing(null);
    } catch (e) { toastError(e); }
  };

  // -------- Itens ---------
  const resetItemForm = () => {
    setEditingItem(null);
    setItemTitulo("");
    setItemCategoria("feature");
    setItemMarkdown("");
    setItemFiles([]);
  };

  const openNewItem = () => { resetItemForm(); setOpenItemModal(true); };
  const openEditItem = (it) => {
    setEditingItem(it);
    setItemTitulo(it.titulo || "");
    setItemCategoria(it.categoria || "feature");
    setItemMarkdown(it.descricaoMarkdown || "");
    setItemFiles([]);
    setOpenItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!editing) return;
    setSavingItem(true);
    try {
      const allFiles = Array.from(itemFiles || []);
      const principal = allFiles[0];
      const extras = allFiles.slice(1, 11); // até 10 por envio (1 principal + 9 extras)
      if (!editingItem) {
        const created = await addItem(editing.id, { titulo: itemTitulo, categoria: itemCategoria, descricaoMarkdown: itemMarkdown, anexo: principal });
        let newItem = created;
        if (extras.length > 0) {
          try {
            const createdAtt = await addItemAttachments(created.id, extras);
            newItem = { ...created, attachments: createdAtt };
          } catch (e) { /* silencioso */ }
        }
        toastSuccess("Item adicionado");
        setEditing(prev => ({ ...prev, itens: [...(prev?.itens||[]), newItem] }));
      } else {
        const upd = await updateItem(editingItem.id, { titulo: itemTitulo, categoria: itemCategoria, descricaoMarkdown: itemMarkdown, anexo: principal });
        let updatedItem = upd;
        if (extras.length > 0) {
          try {
            const createdAtt = await addItemAttachments(upd.id, extras);
            const prevAtt = Array.isArray(upd.attachments) ? upd.attachments : [];
            updatedItem = { ...upd, attachments: [...prevAtt, ...createdAtt] };
          } catch (e) { /* silencioso */ }
        }
        toastSuccess("Item atualizado");
        setEditing(prev => ({ ...prev, itens: prev.itens.map(i => i.id === updatedItem.id ? updatedItem : i) }));
      }
      resetItemForm();
      setOpenItemModal(false);
    } catch (e) { toastError(e); }
    finally { setSavingItem(false); }
  };

  const handleDeleteItem = async (it) => {
    if (!window.confirm("Remover item?")) return;
    try {
      await deleteItem(it.id);
      toastSuccess("Item removido");
      setEditing(prev => ({ ...prev, itens: prev.itens.filter(i => i.id !== it.id) }));
    } catch (e) { toastError(e); }
  };

  const publishedBadge = (r) => r.status === 'published' ? <Chip size="small" label="Publicado" style={{ background: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) }} /> : <Chip size="small" label="Draft" style={{ background: theme.palette.grey[500], color: '#fff' }} />;

  const isNew = (r) => {
    if (!r.releasedAt) return false;
    try { return (Date.now() - new Date(r.releasedAt).getTime()) <= 7*24*60*60*1000; } catch { return false; }
  };

  const sorted = useMemo(() => {
    // Drafts primeiro, depois publicados por releasedAt desc
    return [...releases].sort((a,b) => {
      if (a.status !== b.status) return a.status === 'draft' ? -1 : 1;
      const da = a.releasedAt ? new Date(a.releasedAt).getTime() : 0;
      const db = b.releasedAt ? new Date(b.releasedAt).getTime() : 0;
      return db - da;
    });
  }, [releases]);

  // ===== Roadmap Enhancement Sorting & Coloring =====
  const prioridadePeso = (p) => {
    if (p === 'alta') return 3; if (p === 'media') return 2; if (p === 'baixa') return 1; return 0;
  };
  const roadmapSorted = useMemo(() => {
    return [...roadmap].sort((a,b) => {
      // Ordenar primeiro por status (planned -> in_progress -> done)
      const orderStatus = { planned: 0, in_progress: 1, done: 2 };
      if (orderStatus[a.status] !== orderStatus[b.status]) return orderStatus[a.status] - orderStatus[b.status];
      // Depois por prioridade desc
      const pa = prioridadePeso(a.prioridade); const pb = prioridadePeso(b.prioridade);
      if (pa !== pb) return pb - pa;
      // Depois por ordem (já serve como critério manual) e updatedAt mais recente
      if (a.ordem !== b.ordem) return a.ordem - b.ordem;
      const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return ub - ua;
    });
  }, [roadmap]);

  const statusChipStyle = (status) => {
    const base = { fontSize: 11, height: 22 };
    if (status === 'done') return { ...base, background: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) };
    if (status === 'in_progress') return { ...base, background: theme.palette.info.main, color: theme.palette.getContrastText(theme.palette.info.main) };
    return { ...base, background: theme.palette.warning.main, color: theme.palette.getContrastText(theme.palette.warning.main) };
  };

  const prioridadeChipStyle = (p) => {
    const base = { fontSize: 11, height: 22 };
    if (p === 'alta') return { ...base, background: theme.palette.error.main, color: theme.palette.getContrastText(theme.palette.error.main) };
    if (p === 'media') return { ...base, background: theme.palette.secondary.main, color: theme.palette.getContrastText(theme.palette.secondary.main) };
    if (p === 'baixa') return { ...base, background: theme.palette.grey[600], color: '#fff' };
    return { ...base, background: theme.palette.grey[400], color: '#000' };
  };

  // Helper: total de anexos (principal + extras)
  const totalAttachments = (it) => {
    const base = it?.anexoPath ? 1 : 0;
    const extras = Array.isArray(it?.attachments) ? it.attachments.length : 0;
    return base + extras;
  };

  const categoriaLabels = {
    feature: 'Funcionalidade',
    improvement: 'Melhoria',
    fix: 'Correção',
    security: 'Segurança',
    breaking: 'Breaking',
    deprecated: 'Descontinuado',
    internal: 'Interno'
  };
  // Traduções diretas de status (sem i18n formal)
  const statusLabels = {
    planned: 'Planejado',
    in_progress: 'Em progresso',
    done: 'Concluído'
  };
  const categoriaStyle = (cat) => {
    // Map de categorias para cores consistentes
    const map = {
      feature: theme.palette.primary.main,
      improvement: theme.palette.info.main,
      fix: theme.palette.warning.main,
      security: theme.palette.error.dark,
      breaking: theme.palette.error.main,
      deprecated: theme.palette.grey[700],
      internal: theme.palette.grey[500]
    };
    const bg = map[cat] || theme.palette.grey[600];
    return { background: bg, color: theme.palette.getContrastText(bg), fontSize: 11, height: 22 };
  };

  const handleNewRoadmap = () => { setEditingRoadmapItem(null); setRmTitulo(""); setRmDesc(""); setRmStatus("planned"); setRmPrioridade("media"); setOpenRoadmapModal(true); };
  const openEditRoadmap = async (it) => {
    // Se já temos a descrição, abre direto
    if (it.descricaoMarkdown) {
      setEditingRoadmapItem(it);
      setRmTitulo(it.titulo);
      setRmDesc(it.descricaoMarkdown || "");
      setRmStatus(it.status);
      setRmPrioridade(it.prioridade || "media");
      setOpenRoadmapModal(true);
      return;
    }
    // Fallback: buscar lista completa (sem light) e extrair item para pegar descricaoMarkdown
    try {
      const fullList = await listRoadmap({ light: false });
      const fullItem = fullList.find(r => r.id === it.id) || it;
      setRoadmap(prev => prev.map(r => r.id === fullItem.id ? { ...r, ...fullItem } : r));
      setEditingRoadmapItem(fullItem);
      setRmTitulo(fullItem.titulo);
      setRmDesc(fullItem.descricaoMarkdown || "");
      setRmStatus(fullItem.status);
      setRmPrioridade(fullItem.prioridade || "media");
      setOpenRoadmapModal(true);
    } catch (e) {
      // Em caso de erro, abre com o que já tinha
      setEditingRoadmapItem(it);
      setRmTitulo(it.titulo);
      setRmDesc(it.descricaoMarkdown || "");
      setRmStatus(it.status);
      setRmPrioridade(it.prioridade || "media");
      setOpenRoadmapModal(true);
    }
  };
  const handleSaveRoadmap = async () => {
    if (!rmTitulo) { toastError("Título obrigatório"); return; }
    setSavingRoadmap(true);
    try {
      if (!editingRoadmapItem) {
        const created = await createRoadmapItem({ titulo: rmTitulo, descricaoMarkdown: rmDesc, status: rmStatus, prioridade: rmPrioridade });
        setRoadmap(prev => [...prev, created]);
        toastSuccess("Item criado");
      } else {
        const upd = await updateRoadmapItem(editingRoadmapItem.id, { titulo: rmTitulo, descricaoMarkdown: rmDesc, status: rmStatus, prioridade: rmPrioridade });
        setRoadmap(prev => prev.map(r => r.id === upd.id ? upd : r));
        toastSuccess("Item atualizado");
      }
      setOpenRoadmapModal(false);
    } catch(e){ toastError(e);} finally { setSavingRoadmap(false);} };
  const handleDeleteRoadmap = async (it) => { if(!window.confirm("Remover item?")) return; try { await deleteRoadmapItem(it.id); setRoadmap(prev => prev.filter(r => r.id !== it.id)); toastSuccess("Removido"); } catch(e){ toastError(e);} };

  return (
    <div style={{ padding: 16 }}>
      <Tabs
        value={tab}
        onChange={(e,v)=>setTab(v)}
        style={{ marginBottom:16, color: theme.palette.text.primary }}
        textColor="inherit"
        indicatorColor="primary"
      >
        <Tab label={`Releases (${releases.length})`} />
        <Tab label={`Roadmap (${roadmap.length})`} />
      </Tabs>
      {tab===0 && (
        <div>
          {/* releases existente */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Typography variant="h6" style={{ color: theme.palette.text.primary }}>Gerenciar Releases</Typography>
            <div style={{ flex: 1 }} />
            <Tooltip title="Recarregar"><span>
              <IconButton onClick={load} disabled={loading}><Refresh /></IconButton>
            </span></Tooltip>
            <Button variant="outlined" onClick={() => setOpenExport(true)} style={{ marginRight: 8 }}>Exportar</Button>
            <Button variant="outlined" onClick={() => { setOpenImport(true); setImportFile(null); setImportResult(null); }}>Importar</Button>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleNew}>Nova Release</Button>
          </div>
          <Divider style={{ margin: '12px 0 16px' }} />
          {loading && releases.length === 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:8, paddingTop:8 }}>
              {Array.from({ length:5 }).map((_,i)=>(
                <div key={i} style={{ display:'flex', gap:16, alignItems:'center' }}>
                  <Skeleton variant="rect" width={90} height={24} />
                  <Skeleton variant="rect" width={100} height={24} />
                  <Skeleton variant="rect" width={60} height={24} />
                  <Skeleton variant="rect" width={140} height={24} />
                  <Skeleton variant="rect" width={140} height={24} />
                  <Skeleton variant="rect" width={50} height={24} />
                  <Skeleton variant="rect" width={70} height={32} />
                </div>
              ))}
            </div>
          )}
          {loading && releases.length > 0 && <div style={{ display:'flex', alignItems:'center', gap:8 }}><CircularProgress size={20} /> <span>Atualizando...</span></div>}
          {!loading && sorted.length === 0 && (
            <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
              Nenhuma release ainda.
            </Typography>
          )}
          {!loading && sorted.length > 0 && (
            <div style={{ maxHeight: 420, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: 4 }}>
            <Table size="small" stickyHeader style={{ marginTop: 0 }}>
              <TableHead>
                <TableRow style={{ background: theme.palette.background.default }}>
                  <TableCell style={{ width:120, fontWeight:600 }}>Versão</TableCell>
                  <TableCell style={{ width:120, fontWeight:600 }}>Status</TableCell>
                  <TableCell style={{ width:80, fontWeight:600 }}>Itens</TableCell>
                  <TableCell style={{ width:160, fontWeight:600 }}>Publicado em</TableCell>
                  <TableCell style={{ width:160, fontWeight:600 }}>Última Modif.</TableCell>
                  <TableCell style={{ width:70, fontWeight:600 }}>Novo</TableCell>
                  <TableCell style={{ width:90 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(r => (
                  <TableRow key={r.id} hover style={{ transition:'background .15s' }}>
                    <TableCell style={{ fontWeight:500 }}>{r.version}</TableCell>
                    <TableCell>{publishedBadge(r)}</TableCell>
                    <TableCell>{r.itens ? r.itens.length : (r.itensCount != null ? r.itensCount : (r.totalItens || '-'))}</TableCell>
                    <TableCell>{r.releasedAt ? new Date(r.releasedAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>{r.lastModifiedAt ? new Date(r.lastModifiedAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>{isNew(r) ? <Chip size="small" label="Novo" style={{ background: theme.palette.secondary.main, color: theme.palette.getContrastText(theme.palette.secondary.main) }} /> : ''}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => handleEdit(r)}>Editar</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </div>
      )}
      {tab===1 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <Typography variant="h6" style={{ color: theme.palette.text.primary }}>Gerenciar Roadmap</Typography>
            <div style={{ flex:1 }} />
            <Tooltip title="Recarregar"><span>
              <IconButton onClick={loadRoadmap} disabled={loadingRoadmap}><Refresh /></IconButton>
            </span></Tooltip>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleNewRoadmap}>Novo Item</Button>
          </div>
          <Divider style={{ margin: '12px 0 16px' }} />
          {loadingRoadmap && roadmap.length === 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {Array.from({ length:5 }).map((_,i)=> (
                <div key={i} style={{ display:'flex', gap:16, alignItems:'center' }}>
                  <Skeleton variant="rect" width={220} height={24} />
                  <Skeleton variant="rect" width={90} height={24} />
                  <Skeleton variant="rect" width={80} height={24} />
                  <Skeleton variant="rect" width={160} height={24} />
                  <Skeleton variant="rect" width={120} height={32} />
                </div>
              ))}
            </div>
          )}
          {loadingRoadmap && roadmap.length > 0 && <div style={{ display:'flex', alignItems:'center', gap:8 }}><CircularProgress size={20} /> <span>Atualizando...</span></div>}
          {!loadingRoadmap && roadmap.length === 0 && <Typography variant="body2">Nenhum item no roadmap.</Typography>}
          {!loadingRoadmap && roadmapSorted.length > 0 && (
            <div style={{ maxHeight: 420, overflowY:'auto', border:`1px solid ${borderColor}`, borderRadius:4 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow style={{ background: theme.palette.background.default }}>
                  <TableCell style={{ fontWeight:600 }}>Título</TableCell>
                  <TableCell style={{ fontWeight:600, width:120 }}>Status</TableCell>
                  <TableCell style={{ fontWeight:600, width:120 }}>Prioridade</TableCell>
                  <TableCell style={{ fontWeight:600, width:160 }}>Atualizado</TableCell>
                  <TableCell style={{ width:140 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roadmapSorted.map(it => (
                  <TableRow key={it.id} hover>
                    <TableCell style={{ fontWeight:500 }}>{it.titulo}</TableCell>
                    <TableCell><Chip size="small" label={statusLabels[it.status] || it.status.replace(/_/g,' ')} style={statusChipStyle(it.status)} /></TableCell>
                    <TableCell>{it.prioridade ? <Chip size="small" label={it.prioridade} style={prioridadeChipStyle(it.prioridade)} /> : '-'}</TableCell>
                    <TableCell>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : '-'}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openEditRoadmap(it)}>Editar</Button>
                      <Button size="small" color="secondary" onClick={() => handleDeleteRoadmap(it)}>Remover</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </div>
      )}

      {/* Modal Release */}
      <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Editar Release ${editing.version}` : 'Nova Release'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:16 }}>
              <TextField
                label="Versão"
                value={version}
                onChange={(e) => {
                  // Sanitiza colagem: mantém apenas números e pontos e limita a 3 segmentos
                  let v = (e.target.value || "").replace(/[^0-9.]/g, "");
                  // Reduz pontos consecutivos
                  v = v.replace(/\.\.+/g, ".");
                  // Limita a no máximo 3 segmentos numéricos (2 pontos)
                  const parts = v.split(".").slice(0, 3);
                  // Remove segmentos com caracteres não numéricos (já limpo) e limita tamanho razoável
                  const limited = parts.map(p => p.replace(/\D/g, "").slice(0, 6));
                  v = limited.join(".");
                  setVersion(v);
                  setVersionError(v && !semverRegex.test(v) ? "Use o formato X.X.X (ex.: 1.2.3)" : "");
                }}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End','Enter'
                  ];
                  if (allowedKeys.includes(e.key)) return;
                  // Permitir números
                  if (/^[0-9]$/.test(e.key)) return;
                  // Permitir ponto se ainda houver menos de 2 e não é primeiro char nem imediatamente após ponto
                  if (e.key === '.') {
                    const value = version || "";
                    const dotCount = (value.match(/\./g) || []).length;
                    const selStart = e.target.selectionStart ?? value.length;
                    const selEnd = e.target.selectionEnd ?? value.length;
                    const next = value.slice(0, selStart) + '.' + value.slice(selEnd);
                    const nextStartsWithDot = selStart === 0;
                    const nextHasDoubleDot = /\.\./.test(next);
                    if (dotCount < 2 && !nextStartsWithDot && !nextHasDoubleDot) return;
                  }
                  e.preventDefault();
                }}
                onBlur={() => {
                  // Remove ponto final sobrando
                  let v = version;
                  if (v && /\.$/.test(v)) v = v.replace(/\.+$/, '');
                  setVersion(v);
                  setVersionError(v && !semverRegex.test(v) ? "Use o formato X.X.X (ex.: 1.2.3)" : "");
                }}
                placeholder="1.2.3"
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Versão no formato X.X.X' }}
                error={!!versionError}
                helperText={versionError || "Formato: X.X.X (ex.: 1.2.3)"}
                disabled={!!editing}
                required
              />
            <TextField label="Título (opcional)" value={titulo} onChange={e => setTitulo(e.target.value)} />
          </div>
          {editing && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                <Typography variant="subtitle2" style={{ fontWeight:600 }}>Itens</Typography>
                <Button size="small" variant="contained" color="primary" onClick={openNewItem} startIcon={<Add />}>Adicionar Item</Button>
                {editing.status === 'draft' && editing.itens?.length === 0 && (
                  <Typography variant="caption" style={{ marginLeft:8 }}>Adicione itens antes de publicar.</Typography>
                )}
              </div>
              <div style={{ display:'grid', gap:12 }}>
                {(editing.itens||[]).map(it => (
                  <div key={it.id} style={{ border:`1px solid ${borderColor}`, borderRadius:6, padding:10, background: theme.palette.background.paper, boxShadow:'0 1px 2px rgba(0,0,0,0.06)' }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <Chip size="small" label={categoriaLabels[it.categoria] || it.categoria} style={categoriaStyle(it.categoria)} />
                      <Typography variant="body2" style={{ fontWeight:600 }}>{it.titulo}</Typography>
                      {totalAttachments(it) > 0 && (
                        <Chip size="small" label={`${totalAttachments(it)} ${totalAttachments(it) === 1 ? 'anexo' : 'anexos'}`} />
                      )}
                      <div style={{ flex:1 }} />
                      <Button size="small" onClick={() => openEditItem(it)}>Editar</Button>
                      <Button size="small" onClick={() => handleDeleteItem(it)} color="secondary">Remover</Button>
                    </div>
                    {/* Contagem de anexos unificada; gestão detalhada ocorre no modal de edição do item */}
                    {it.descricaoMarkdown && (
                      <div style={{ fontSize:12, marginTop:6, whiteSpace:'pre-wrap', lineHeight:1.4 }}>
                        <Markdown>{it.descricaoMarkdown}</Markdown>
                      </div>
                    )}
                  </div>
                ))}
                {editing.itens?.length === 0 && <Typography variant="body2" color="textSecondary">Nenhum item ainda.</Typography>}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {editing?.status === 'draft' && (
            <Button onClick={handleDeleteRelease} style={{ marginRight:'auto' }} color="secondary">Excluir</Button>
          )}
          {editing?.status === 'published' && (
            <div style={{ marginRight:'auto', display:'flex', gap:8 }}>
              <Button onClick={handleUnpublish} variant="outlined" color="default">Cancelar Publicação</Button>
              <Button onClick={handleForceDelete} variant="contained" color="secondary">Excluir (Publicado)</Button>
            </div>
          )}
          {editing?.status === 'draft' && (
            <Button onClick={handlePublish} color="primary" startIcon={<Publish />}>Publicar</Button>
          )}
          <Button onClick={handleSaveRelease} color="primary" startIcon={<Save />} disabled={savingRelease || !semverRegex.test(version)}>{savingRelease ? 'Salvando...' : 'Salvar'}</Button>
          <Button onClick={() => setOpenEditor(false)} startIcon={<Close />}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Exportar */}
      <Dialog open={openExport} onClose={() => setOpenExport(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Exportar Releases (ZIP)</DialogTitle>
        <DialogContent dividers>
          <div style={{ display:'grid', gap:12 }}>
            <TextField select label="Modo" value={exportMode} onChange={e => setExportMode(e.target.value)} fullWidth>
              <MenuItem value="all">Tudo</MenuItem>
              <MenuItem value="last">Últimas X</MenuItem>
            </TextField>
            {exportMode === 'last' && (
              <TextField
                label="Quantidade (X)"
                type="number"
                inputProps={{ min:1, max:100 }}
                value={exportCount}
                onChange={e => setExportCount(Math.max(1, Math.min(100, Number(e.target.value||1))))}
                fullWidth
              />
            )}
            <Typography variant="caption" color="textSecondary">
              O arquivo gerado incluirá: releases ({exportMode === 'last' ? `últimas ${exportCount}` : 'todas'}) com itens e anexos, e o roadmap completo.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExport(false)} startIcon={<Close />}>Fechar</Button>
          <Button
            color="primary"
            startIcon={<Save />}
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const { blob, filename } = await exportReleasesZip({ mode: exportMode, count: exportCount });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = filename || 'changelog-export.zip';
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                toastSuccess('Exportação concluída');
                setOpenExport(false);
              } catch (e) {
                toastError(e);
              } finally { setExporting(false); }
            }}
          >{exporting ? 'Exportando...' : 'Exportar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Importar */}
      <Dialog open={openImport} onClose={() => setOpenImport(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Releases (ZIP)</DialogTitle>
        <DialogContent dividers>
          <div style={{ display:'grid', gap:12 }}>
            <input
              type="file"
              accept="application/zip,.zip"
              onChange={e => { setImportFile((e.target.files && e.target.files[0]) || null); setImportResult(null); }}
            />
            <Typography variant="caption" color="textSecondary">
              A importação cria apenas o que não existe nesta empresa. Anexos serão gravados com deduplicação por checksum.
            </Typography>
            {importResult && (
              <div style={{ border:`1px solid ${borderColor}`, borderRadius:4, padding:8 }}>
                <Typography variant="subtitle2" style={{ fontWeight:600, marginBottom:6 }}>Resumo</Typography>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:8, fontSize:13 }}>
                  <div>Releases criadas: <b>{importResult?.releases?.created ?? 0}</b></div>
                  <div>Releases puladas: <b>{importResult?.releases?.skipped ?? 0}</b></div>
                  <div>Roadmap criados: <b>{importResult?.roadmap?.created ?? 0}</b></div>
                  <div>Roadmap pulados: <b>{importResult?.roadmap?.skipped ?? 0}</b></div>
                  <div>Anexos gravados: <b>{importResult?.attachments?.written ?? 0}</b></div>
                  <div>Anexos reutilizados: <b>{importResult?.attachments?.reused ?? 0}</b></div>
                </div>
                {(importResult?.warnings?.length > 0) && (
                  <div style={{ marginTop:8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight:600 }}>Avisos</Typography>
                    <ul style={{ margin:0, paddingLeft:18 }}>
                      {importResult.warnings.map((w,i) => <li key={i} style={{ fontSize:12 }}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {(importResult?.errors?.length > 0) && (
                  <div style={{ marginTop:8 }}>
                    <Typography variant="subtitle2" style={{ fontWeight:600, color: theme.palette.error.main }}>Erros</Typography>
                    <ul style={{ margin:0, paddingLeft:18 }}>
                      {importResult.errors.map((er,i) => <li key={i} style={{ fontSize:12 }}>{er}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImport(false)} startIcon={<Close />}>Fechar</Button>
          <Button
            color="primary"
            startIcon={<Save />}
            disabled={importing || !importFile}
            onClick={async () => {
              setImporting(true);
              try {
                const res = await importReleasesZip(importFile);
                setImportResult(res);
                toastSuccess('Importação concluída');
                // Opcional: recarregar listagem
                await load();
              } catch (e) {
                toastError(e);
              } finally { setImporting(false); }
            }}
          >{importing ? 'Importando...' : 'Importar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Item */}
      <Dialog open={openItemModal} onClose={() => setOpenItemModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Editar Item' : 'Novo Item'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <TextField label="Título" value={itemTitulo} onChange={e => setItemTitulo(e.target.value)} fullWidth required />
            <TextField select label="Categoria" value={itemCategoria} onChange={e => setItemCategoria(e.target.value)} fullWidth>
              {categorias.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Descrição" value={itemMarkdown} onChange={e => setItemMarkdown(e.target.value)} multiline rows={6} fullWidth />
            <div>
              <Typography variant="caption">Anexos (até 10 arquivos por envio)</Typography><br />
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,video/mp4"
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 10) {
                    toastError("Selecione no máximo 10 arquivos por envio");
                  }
                  setItemFiles(files.slice(0,10));
                }}
              />
              {itemFiles && itemFiles.length > 0 && (
                <Typography variant="caption" color="textSecondary">{itemFiles.length} arquivo(s) selecionado(s)</Typography>
              )}
            </div>

            {editingItem && (
              <div style={{ marginTop: 8 }}>
                <Typography variant="subtitle2" style={{ fontWeight:600, marginBottom: 6 }}>Anexos existentes</Typography>
                {totalAttachments(editingItem) === 0 ? (
                  <Typography variant="caption" color="textSecondary">Nenhum anexo</Typography>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: 10
                    }}
                  >
                    {(() => {
                      const isImage = (nameOrMime) => {
                        if (!nameOrMime) return false;
                        const s = String(nameOrMime).toLowerCase();
                        return s.includes('image/') || /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.svg)$/i.test(s);
                      };
                      const isVideo = (nameOrMime) => {
                        if (!nameOrMime) return false;
                        const s = String(nameOrMime).toLowerCase();
                        return s.includes('video/') || /\.mp4$/i.test(s);
                      };
                      const isPdf = (nameOrMime) => {
                        if (!nameOrMime) return false;
                        const s = String(nameOrMime).toLowerCase();
                        return s.includes('application/pdf') || /\.pdf$/i.test(s);
                      };

                      const getBase = () => {
                        const raw = getBackendUrl() || 'http://localhost:8080';
                        // Remove barras finais e qualquer sufixo "/api" ou "/api/..."
                        const cleaned = raw.replace(/\/+$/, '').replace(/\/api(?:\/.*)?$/i, '');
                        // Preferir localhost em dev Windows
                        try {
                          const isDev = String(process.env.NODE_ENV) !== 'production';
                          const isWin = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent || navigator.platform || '');
                          const u = new URL(cleaned);
                          if (isDev && isWin && (u.hostname === '127.0.0.1')) {
                            return `${u.protocol}//localhost${u.port ? ':' + u.port : ''}`;
                          }
                        } catch {}
                        return cleaned;
                      };
                      const normalizePath = (p) => {
                        if (!p) return '';
                        let s = String(p);
                        // se já for URL absoluta, retornar como está
                        if (/^https?:\/\//i.test(s)) return s;
                        // normalizar barras invertidas do Windows
                        s = s.replace(/\\/g, '/');
                        // Se veio caminho absoluto de Windows contendo /public/, recortar a partir de /public/
                        const idx = s.toLowerCase().indexOf('/public/');
                        if (idx >= 0) {
                          s = s.slice(idx + '/public/'.length);
                        }
                        // remover prefixos /public ou public/
                        s = s.replace(/^\/?public\/?/i, '');
                        // remover barra inicial
                        s = s.replace(/^\//, '');
                        return s;
                      };
                      const encodeSegments = (p) => {
                        if (!p) return '';
                        return p.split('/').map(seg => encodeURIComponent(seg)).join('/');
                      };
                      const buildPublicUrl = (p) => {
                        if (!p) return '';
                        if (/^https?:\/\//i.test(String(p))) return String(p);
                        const base = getBase();
                        const clean = normalizePath(p);
                        const encoded = encodeSegments(clean);
                        return `${base}/public/${encoded}`;
                      };
                      const buildFallbackUrl = (p) => {
                        const base = getBase();
                        const clean = normalizePath(p);
                        const hasCompany = /^company\d+\//i.test(clean);
                        const prefixed = hasCompany ? clean : (currentUser?.companyId ? `company${currentUser.companyId}/${clean}` : clean);
                        const encoded = encodeSegments(prefixed);
                        return `${base}/public/${encoded}`;
                      };
                      const candidateUrls = (p) => {
                        const candidates = [];
                        const base = getBase();
                        if (!p) return candidates;
                        if (/^https?:\/\//i.test(String(p))) return [String(p)];
                        const clean = normalizePath(p);
                        const encoded = encodeSegments(clean);
                        const withPublic = `${base}/public/${encoded}`;
                        candidates.push(withPublic);
                        const hasCompany = /^company\d+\//i.test(clean);
                        if (!hasCompany && currentUser?.companyId) {
                          candidates.push(`${base}/public/company${currentUser.companyId}/${encoded}`);
                        }
                        // Também tentar sem prefixo company caso venha por engano
                        const stripped = clean.replace(/^company\d+\//i, '');
                        if (stripped !== clean) {
                          const strippedEncoded = encodeSegments(stripped);
                          candidates.push(`${base}/public/${strippedEncoded}`);
                        }
                        // Variantes de host: localhost vs 127.0.0.1
                        try {
                          const u = new URL(base);
                          const altHost = u.hostname === 'localhost' ? '127.0.0.1' : (u.hostname === '127.0.0.1' ? 'localhost' : null);
                          if (altHost) {
                            const altBase = `${u.protocol}//${altHost}${u.port ? ':' + u.port : ''}`;
                            candidates.push(`${altBase}/public/${encoded}`);
                            if (!hasCompany && currentUser?.companyId) candidates.push(`${altBase}/public/company${currentUser.companyId}/${encoded}`);
                            if (stripped !== clean) candidates.push(`${altBase}/public/${encodeSegments(stripped)}`);
                          }
                        } catch {}
                        // Remover duplicados preservando ordem
                        return Array.from(new Set(candidates));
                      };

                      const unified = [];
                      if (editingItem.anexoPath) {
                        unified.push({ id: '__principal__', path: editingItem.anexoPath, mime: editingItem.anexoMime, isPrincipal: true });
                      }
                      if (Array.isArray(editingItem.attachments)) {
                        for (const att of editingItem.attachments) {
                          unified.push({ id: att.id, path: att.path, mime: att.mime, isPrincipal: false });
                        }
                      }

                      return unified.map((file) => {
                        const fkey = String(file.id || file.path);
                        const urls = candidateUrls(file.path);
                        const attempt = thumbAttempt[fkey] || 0;
                        const currentUrl = urls[Math.min(attempt, urls.length - 1)] || urls[0];
                        const showImg = isImage(file.mime || file.path) && !failedThumbs[fkey];
                        return (
                        <div
                          key={fkey}
                          style={{
                            position: 'relative',
                            border: `1px solid ${borderColor}`,
                            borderRadius: 6,
                            overflow: 'hidden',
                            background: theme.palette.background.paper,
                            minHeight: 120
                          }}
                        >
                          <a
                            href={currentUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                          >
                            {showImg ? (
                              <img
                                src={currentUrl}
                                alt="anexo"
                                style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                                onError={() => {
                                  // avançar tentativa usando estado anterior
                                  setThumbAttempt(prev => {
                                    const next = (prev[fkey] || 0) + 1;
                                    if (next >= urls.length) {
                                      setFailedThumbs(p2 => ({ ...p2, [fkey]: true }));
                                    }
                                    return { ...prev, [fkey]: next };
                                  });
                                }}
                              />
                            ) : (
                              <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                                {isVideo(file.mime || file.path) ? 'Vídeo' : isPdf(file.mime || file.path) ? 'PDF' : 'Arquivo'}
                              </div>
                            )}
                            <div style={{ padding: '4px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="caption" noWrap style={{ maxWidth: '85%' }}>
                                {file.isPrincipal ? 'Anexo (principal)' : 'Anexo'}
                              </Typography>
                              {file.isPrincipal && (
                                <Chip size="small" label="Principal" />
                              )}
                            </div>
                          </a>
                          {!file.isPrincipal && (
                            <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                style={{ minWidth: 0, padding: '2px 6px' }}
                                disabled={deletingAttachmentId === file.id}
                                onClick={async () => {
                                  if (!window.confirm('Remover anexo?')) return;
                                  try {
                                    setDeletingAttachmentId(file.id);
                                    await deleteItemAttachment(editingItem.id, file.id);
                                    const updatedAttachments = (editingItem.attachments || []).filter(a => a.id !== file.id);
                                    setEditingItem(prev => ({ ...prev, attachments: updatedAttachments }));
                                    setEditing(prev => ({ ...prev, itens: prev.itens.map(i => i.id === editingItem.id ? { ...i, attachments: updatedAttachments } : i) }));
                                    toastSuccess('Anexo removido');
                                  } catch (e) { toastError(e); } finally { setDeletingAttachmentId(null); }
                                }}
                              >
                                {deletingAttachmentId === file.id ? '...' : <Close style={{ fontSize: 16 }} />}
                              </Button>
                            </div>
                          )}
                        </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

          </div>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveItem} color="primary" startIcon={<Save />} disabled={savingItem}>{savingItem ? 'Salvando...' : 'Salvar'}</Button>
          <Button onClick={() => setOpenItemModal(false)} startIcon={<Close />}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Roadmap */}
      <Dialog open={openRoadmapModal} onClose={() => setOpenRoadmapModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoadmapItem ? 'Editar Item Roadmap' : 'Novo Item Roadmap'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <TextField label="Título" value={rmTitulo} onChange={e=>setRmTitulo(e.target.value)} fullWidth required />
            <TextField label="Descrição Markdown" value={rmDesc} onChange={e=>setRmDesc(e.target.value)} fullWidth multiline rows={5} />
            <TextField select label="Status" value={rmStatus} onChange={e=>setRmStatus(e.target.value)} fullWidth>
              <MenuItem value="planned">Planejado</MenuItem>
              <MenuItem value="in_progress">Em progresso</MenuItem>
              <MenuItem value="done">Concluído</MenuItem>
            </TextField>
            <TextField select label="Prioridade" value={rmPrioridade} onChange={e=>setRmPrioridade(e.target.value)} fullWidth>
              <MenuItem value="baixa">Baixa</MenuItem>
              <MenuItem value="media">Média</MenuItem>
              <MenuItem value="alta">Alta</MenuItem>
            </TextField>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveRoadmap} color="primary" startIcon={<Save />} disabled={savingRoadmap}>{savingRoadmap ? 'Salvando...' : 'Salvar'}</Button>
          <Button onClick={()=>setOpenRoadmapModal(false)} startIcon={<Close />}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReleasesAdmin;
